import { supabase } from './supabase.js'

/**
 * Attachments — a receipt on a payable, a photo on a note, a quote on a build.
 *
 * The table predates the bucket and was built for links as well as files, so
 * `kind` says which one a row is and `url` means two different things:
 * a real URL when kind is 'link', otherwise the object path inside the
 * private bucket. Nothing in the bucket is publicly readable, so a file is
 * shown through a signed URL minted at the moment it is needed.
 *
 * Every path starts with the owner's id, because that first folder is what
 * the storage policies check. Get that wrong and the upload is rejected,
 * which is the right failure.
 */

export const BUCKET = 'attachments'

/* An hour is longer than anyone looks at a receipt and short enough that a
   copied link is not a lasting key to the file. */
const SIGNED_FOR = 3600

/* The free tier's own limit. Checked here so an oversized file fails before
   the upload starts rather than after a minute of waiting. */
export const MAX_BYTES = 50 * 1024 * 1024

/* Long edge of a stored photo. A receipt is legible well below this; the
   point of the cap is that a 4MB camera file does not spend 4MB of the 1GB. */
const MAX_EDGE = 1600
const JPEG_QUALITY = 0.82

export function kindOf(file) {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  return 'file'
}

function extOf(name, fallback) {
  const dot = name.lastIndexOf('.')
  if (dot > 0 && dot < name.length - 1) return name.slice(dot + 1).toLowerCase()
  return fallback
}

/**
 * Shrink a photo in the browser before it is uploaded.
 *
 * Returns the original untouched if it is already small, if it is not an
 * image, or if anything at all goes wrong — a failed resize must never cost
 * him the attachment. GIFs are left alone because redrawing one to a canvas
 * throws away the animation.
 */
export async function downscale(file) {
  if (!file.type.startsWith('image/')) return file
  if (file.type === 'image/gif') return file
  if (file.size < 400 * 1024) return file

  try {
    const bitmap = await createImageBitmap(file)
    const { width, height } = bitmap
    const scale = Math.min(1, MAX_EDGE / Math.max(width, height))
    if (scale === 1 && file.size < 1.5 * 1024 * 1024) {
      bitmap.close?.()
      return file
    }

    const w = Math.round(width * scale)
    const h = Math.round(height * scale)
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h)
    bitmap.close?.()

    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', JPEG_QUALITY))
    if (!blob || blob.size >= file.size) return file

    const base = file.name.replace(/\.[^.]+$/, '')
    return new File([blob], base + '.jpg', { type: 'image/jpeg' })
  } catch {
    return file
  }
}

export async function getAttachments(targetType, targetId) {
  const { data, error } = await supabase
    .from('attachments')
    .select('*')
    .eq('target_type', targetType)
    .eq('target_id', targetId)
    .order('created_at')
  if (error) throw error
  return data ?? []
}

/** One query for a whole list, so a screen of rows is not a query per row. */
export async function countsFor(targetType, ids) {
  if (!ids.length) return {}
  const { data, error } = await supabase
    .from('attachments')
    .select('target_id')
    .eq('target_type', targetType)
    .in('target_id', ids)
  if (error) throw error
  const out = {}
  for (const r of data ?? []) out[r.target_id] = (out[r.target_id] || 0) + 1
  return out
}

export async function uploadAttachment(targetType, targetId, file, label) {
  if (file.size > MAX_BYTES) {
    throw new Error('That file is over 50MB, which is more than the storage allows.')
  }

  const { data: auth } = await supabase.auth.getUser()
  const uid = auth?.user?.id
  if (!uid) throw new Error('Signed out — sign in again and retry.')

  const small = await downscale(file)
  const kind = kindOf(small)
  const ext = extOf(small.name, 'bin')
  const path = [uid, targetType, targetId, crypto.randomUUID() + '.' + ext].join('/')

  const up = await supabase.storage.from(BUCKET).upload(path, small, {
    contentType: small.type || 'application/octet-stream',
    upsert: false,
  })
  if (up.error) throw up.error

  const { data, error } = await supabase
    .from('attachments')
    .insert({
      target_type: targetType,
      target_id: targetId,
      kind,
      url: path,
      label: label || file.name,
      bytes: small.size,
    })
    .select()
    .single()

  /* A row that failed to save would leave a file nobody can find. Take the
     file back out so the bucket never fills with orphans. */
  if (error) {
    await supabase.storage.from(BUCKET).remove([path]).catch(() => {})
    throw error
  }
  return data
}

/** A plain URL attachment — no bytes, no bucket, just a pointer. */
export async function addLinkAttachment(targetType, targetId, url, label) {
  const clean = url.trim()
  if (!clean) throw new Error('No address given.')
  const { data, error } = await supabase
    .from('attachments')
    .insert({
      target_type: targetType,
      target_id: targetId,
      kind: 'link',
      url: clean,
      label: label?.trim() || clean.replace(/^https?:\/\//, '').slice(0, 60),
    })
    .select()
    .single()
  if (error) throw error
  return data
}

/**
 * A URL that will actually open. Link rows are already addresses; file rows
 * need signing, and signing is a network call, so this is asked for on tap
 * rather than for every row on load.
 */
export async function openableUrl(row) {
  if (row.kind === 'link') return row.url
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(row.url, SIGNED_FOR)
  if (error) throw error
  return data.signedUrl
}

/** Thumbnails for a whole list in one call, so a gallery is one round trip. */
export async function signMany(rows) {
  const files = rows.filter((r) => r.kind !== 'link')
  if (!files.length) return {}
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrls(files.map((r) => r.url), SIGNED_FOR)
  if (error) return {}
  const out = {}
  data?.forEach((d, i) => { if (d.signedUrl) out[files[i].id] = d.signedUrl })
  return out
}

/**
 * Remove the row first, then the file.
 *
 * That order is deliberate. A row with no file is a visible broken
 * attachment he can delete again; a file with no row is invisible and stays
 * in the bucket forever.
 */
export async function removeAttachment(row) {
  const { error } = await supabase.from('attachments').delete().eq('id', row.id)
  if (error) throw error
  if (row.kind !== 'link') {
    await supabase.storage.from(BUCKET).remove([row.url]).catch(() => {})
  }
}
